import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Popconfirm,
  theme,
  Typography,
} from 'antd';
import { EmailListFormComponent } from './EmailListFormComponent';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { t as translate } from 'i18next';
import { useTranslation } from 'react-i18next';

interface DepartmentFormComponentProps {
  departmentNames: RefObject<Array<string>>;
  // the study line names are kept by department, so they are updated when a department is deleted
  studyLineNames?: RefObject<Array<Array<string>>>;
}

interface DepartmentFieldValue {
  departmentName?: string;
}

const validateDepartmentName = (
  departmentNames: string[],
  value: string
): Promise<void> => {
  if (value === '') {
    return Promise.resolve();
  }

  return departmentNames.filter((name) => name === value).length === 1
    ? Promise.resolve()
    : Promise.reject(new Error(translate('validator.depratmentNameUsed')));
};

export const DepartmentFormComponent = ({
  departmentNames,
  studyLineNames,
}: DepartmentFormComponentProps): ReactNode => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const form = Form.useFormInstance();
  const watchedDepartments = Form.useWatch(['departments']);
  // the panels the user opened, by department; a panel with an error is always shown, so the error can not stay hidden
  const [openPanels, setOpenPanels] = useState<Record<number, string[]>>({});

  const departments = useMemo(
    () => (watchedDepartments as DepartmentFieldValue[] | undefined) ?? [],
    [watchedDepartments]
  );

  useEffect(() => {
    /* eslint-disable */
    departmentNames.current = departments.map(
      (department) => department?.departmentName ?? ''
    );
    /* eslint-enable */
  }, [departmentNames, departments]);

  const panelHasError = (department: number, property: string): boolean =>
    form
      .getFieldsError()
      .some(
        ({ name, errors }) =>
          errors.length > 0 &&
          name[0] === 'departments' &&
          name[1] === department &&
          name[2] === property
      );

  const getActivePanels = (fieldKey: number, department: number): string[] => {
    const active = new Set(openPanels[fieldKey] ?? []);
    if (panelHasError(department, 'deans')) {
      active.add('Deans');
    }
    if (panelHasError(department, 'projectCoordinators')) {
      active.add('ProjectCoordinators');
    }
    return [...active];
  };

  // The study lines are listed by department and the sessions one after the other for all study lines,
  // so deleting a department also deletes its study lines and their sessions, to keep the lists in step
  const deleteDepartment = (
    index: number,
    remove: (index: number) => void
  ): void => {
    const studyLines =
      (form.getFieldValue('studyLines') as unknown[][] | undefined) ?? [];
    const sessions =
      (form.getFieldValue('sessions') as unknown[] | undefined) ?? [];
    const firstSession = studyLines
      .slice(0, index)
      .reduce((count, lines) => count + (lines?.length ?? 0), 0);
    const sessionCount = studyLines[index]?.length ?? 0;

    form.setFieldsValue({
      studyLines: studyLines.filter((_, i) => i !== index),
      sessions: sessions.filter(
        (_, i) => i < firstSession || i >= firstSession + sessionCount
      ),
    });
    studyLineNames?.current.splice(index, 1);
    remove(index);
  };

  // A new department is added at the top, next to the button, without study lines yet;
  // the study lines are listed by department, so an empty list is put in front for it
  const addDepartment = (
    add: (defaultValue?: unknown, insertIndex?: number) => void
  ): void => {
    const studyLines =
      (form.getFieldValue('studyLines') as unknown[][] | undefined) ?? [];
    form.setFieldValue('studyLines', [[], ...studyLines]);
    studyLineNames?.current.unshift([]);
    add(undefined, 0);
  };

  return (
    <Form.List name={['departments']}>
      {(fields, fieldOperations) => (
        <>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginBottom: 16 }}
            onClick={() => addDepartment(fieldOperations.add)}
          >
            {t('uniForm.depComp.addDepartment')}
          </Button>
          {fields.map((field, index) => (
            <Card
              key={field.key}
              size="small"
              style={{ marginBottom: 16 }}
              styles={{ header: { background: token.colorInfoBg } }}
              title={t('uniForm.depComp.cardTitle', { number: index + 1 })}
              extra={
                <Popconfirm
                  title={t('uniForm.depComp.deleteDepartmentTitle')}
                  description={t('uniForm.depComp.deleteDepartmentDescription')}
                  okText={t('uniForm.depComp.deleteDepartmentOk')}
                  cancelText={t('uniForm.depComp.deleteDepartmentCancel')}
                  okButtonProps={{ danger: true }}
                  onConfirm={() =>
                    deleteDepartment(field.name, fieldOperations.remove)
                  }
                >
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    aria-label={t('uniForm.depComp.deleteDepartment')}
                    title={t('uniForm.depComp.deleteDepartment')}
                  />
                </Popconfirm>
              }
            >
              <Form.Item
                label={t('uniForm.depComp.name')}
                tooltip={t('uniForm.help.departmentName')}
                rules={[
                  {
                    required: true,
                    message: t('validator.departmentNameEmpty'),
                  },
                  {
                    validator: (_, value): Promise<void> => {
                      return validateDepartmentName(
                        departmentNames.current,
                        value
                      );
                    },
                  },
                ]}
                name={[field.name, 'departmentName']}
              >
                <Input />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Collapse
                    ghost
                    activeKey={getActivePanels(field.key, field.name)}
                    onChange={(keys) =>
                      setOpenPanels((current) => ({
                        ...current,
                        [field.key]: [keys].flat(),
                      }))
                    }
                    items={[
                      {
                        key: 'Deans',
                        // rendered while closed, so its rules are checked when the form is saved
                        forceRender: true,
                        label: t('uniForm.depComp.deansLabel'),
                        children: (
                          <>
                            <Typography.Paragraph type="secondary">
                              {t('uniForm.help.deans')}
                            </Typography.Paragraph>
                            <EmailListFormComponent
                              buttonTitle={t('uniForm.depComp.addDean')}
                              propertyReference="deans"
                              parentReference={[field.name]}
                              emailType={t('uniForm.depComp.deanEmailType')}
                            />
                          </>
                        ),
                      },
                      {
                        key: 'ProjectCoordinators',
                        forceRender: true,
                        label: t('uniForm.depComp.projectCoordinatorsLabel'),
                        children: (
                          <>
                            <Typography.Paragraph type="secondary">
                              {t('uniForm.help.projectCoordinators')}
                            </Typography.Paragraph>
                            <EmailListFormComponent
                              buttonTitle={t(
                                'uniForm.depComp.addProjectCoordinator'
                              )}
                              propertyReference="projectCoordinators"
                              parentReference={[field.name]}
                              emailType={t(
                                'uniForm.depComp.projectCoordinatorEmailType'
                              )}
                              requiredMessage={t(
                                'validator.projectCoordinatorRequired'
                              )}
                            />
                          </>
                        ),
                      },
                    ]}
                  />
                )}
              </Form.Item>
            </Card>
          ))}
        </>
      )}
    </Form.List>
  );
};
